'use client'

import { useRef } from 'react'
import {
  ForwardIcon,
  PauseIcon,
  PlayIcon,
} from '@heroicons/react/24/solid'
import { gsap, reducedMotion, useGSAP } from '@/lib/gsap'
import { SOUNDSCAPE_BY_ID } from './catalog'
import { SoundscapeIcon } from './SoundscapeIcons'
import { useFocusSoundscape } from './FocusSoundscapeProvider'

export function formatCountdown(totalSeconds: number) {
  const safe = Math.max(0, Math.floor(totalSeconds))
  const hours = Math.floor(safe / 3600)
  const minutes = Math.floor((safe % 3600) / 60)
  const seconds = safe % 60
  return hours > 0
    ? `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    : `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
}

export default function SoundscapeDock() {
  const {
    timeLeft,
    currentMixName,
    selectedTracks,
    isSoundscapePlaying,
    dockCollapsed,
    setDockCollapsed,
    setStudioOpen,
    toggleSoundscape,
    nextMix,
  } = useFocusSoundscape()
  const rootRef = useRef<HTMLDivElement>(null)
  const pointerStartRef = useRef<number | null>(null)
  const suppressClickRef = useRef(false)
  const primaryTrack = selectedTracks[0]
  const trackNames = selectedTracks
    .map(track => SOUNDSCAPE_BY_ID.get(track.id)?.name)
    .filter(Boolean)
    .join(' · ')

  useGSAP(() => {
    if (!rootRef.current) return
    gsap.set(rootRef.current, { xPercent: dockCollapsed ? 0 : -50, x: 0 })
    if (reducedMotion()) return
    gsap.fromTo(rootRef.current,
      { autoAlpha: 0, y: 18, scale: 0.96 },
      { autoAlpha: 1, y: 0, scale: 1, duration: 0.42, ease: 'power3.out' },
    )
  }, { scope: rootRef, dependencies: [dockCollapsed], revertOnUpdate: true })

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('button')) return
    pointerStartRef.current = event.clientX
    suppressClickRef.current = false
    if (typeof event.currentTarget.setPointerCapture === 'function') {
      event.currentTarget.setPointerCapture(event.pointerId)
    }
  }

  const onPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    const start = pointerStartRef.current
    pointerStartRef.current = null
    if (start === null) return
    const distance = event.clientX - start
    if (distance < -54) {
      suppressClickRef.current = true
      setDockCollapsed(true)
    }
  }

  if (dockCollapsed) {
    return (
      <div
        ref={rootRef}
        className={`soundscape-dock soundscape-dock-collapsed ${isSoundscapePlaying ? 'is-playing' : ''}`}
        data-testid="soundscape-dock-collapsed"
      >
        <button
          className="soundscape-peek"
          onClick={() => setDockCollapsed(false)}
          aria-label={`展开声景播放器，剩余 ${formatCountdown(timeLeft)}`}
        >
          <span className="soundscape-peek-disc" aria-hidden="true" />
          <strong>{formatCountdown(timeLeft)}</strong>
        </button>
      </div>
    )
  }

  return (
    <div
      ref={rootRef}
      className={`soundscape-dock soundscape-dock-expanded ${isSoundscapePlaying ? 'is-playing' : ''}`}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onClick={() => {
        if (suppressClickRef.current) {
          suppressClickRef.current = false
          return
        }
        setStudioOpen(true)
      }}
      data-testid="soundscape-dock-expanded"
      role="button"
      tabIndex={0}
      aria-label="打开专注声景控制台"
      title="点击打开控制台，向左滑动收起"
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          setStudioOpen(true)
        }
      }}
    >
      <span className={`soundscape-orb tone-${SOUNDSCAPE_BY_ID.get(primaryTrack?.id)?.tone || 'blue'} ${isSoundscapePlaying ? 'playing' : ''}`}>
        {primaryTrack && <SoundscapeIcon id={primaryTrack.id} />}
      </span>
      <span className="soundscape-dock-copy">
        <strong>{formatCountdown(timeLeft)}</strong>
        <span>{currentMixName}</span>
        <small>{trackNames}</small>
      </span>
      <button
        className="soundscape-dock-primary"
        onClick={event => { event.stopPropagation(); toggleSoundscape() }}
        aria-label={isSoundscapePlaying ? '暂停白噪音' : '播放白噪音'}
      >
        {isSoundscapePlaying ? <PauseIcon /> : <PlayIcon />}
      </button>
      <button
        className="soundscape-dock-next"
        onClick={event => { event.stopPropagation(); nextMix() }}
        aria-label="下一组声景"
      >
        <ForwardIcon />
      </button>
    </div>
  )
}
